import React from 'react';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';
import { ExternalLink, Github, ArrowRight } from 'lucide-react';
import Finance from '../assets/Finance.png';
import Medical from '../assets/Medical.png';
import Auto from '../assets/Auto.png';
import Logistics from '../assets/Logistics.png';

const Portfolio = () => {
  const projects = [
    {
      title: 'FinTech Banking Platform',
      category: 'Financial Services',
      description: 'A comprehensive digital banking solution with real-time transactions, advanced security, and mobile-first design.',
      image: Finance,
      technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
      results: ['40% faster transactions', '99.9% uptime', '2M+ users']
    },
    {
      title: 'Healthcare Management System',
      category: 'Healthcare',
      description: 'An integrated healthcare platform connecting patients, doctors, and administrators with seamless workflow management.',
      image: Medical,
      technologies: ['Vue.js', 'Python', 'MongoDB', 'Azure'],
      results: ['60% efficiency gain', 'HIPAA compliant', '500+ hospitals']
    },
    {
      title: "Automotive",
      category: "Automotive",
      description: "A high-performance, multi-vendor automotive marketplace built for modern dealerships, workshops, and auto parts providers.",
      image: Auto,
      technologies: ["Next.js", "Express", "Redis", "Stripe"],
      results: ["300% sales increase", "1M+ products", "50+ vendors"]
    },
    {
      title: "Travel and Logistics",
      category: "Travel",
      description: "A smart logistics and travel management system powered by IoT, enabling predictive maintenance, real-time fleet tracking, and automated operational reporting.",
      image: Logistics,
      technologies: ["React", "Python", "InfluxDB", "Docker"],
      results: ["25% cost reduction", "Zero downtime", "Real-time insights"]
    }
    
  ];

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Portfolio</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Discover how we've helped businesses across industries achieve their digital transformation goals with innovative solutions.
          </motion.p>
        </AnimatedSection>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <motion.img
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover"
                />
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
                  className="absolute top-4 left-4"
                >
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.category}
                  </span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="absolute top-4 right-4"
                >
                  <div className="flex space-x-2">
                    <motion.button 
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-600" />
                    </motion.button>
                    <motion.button 
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                    >
                      <Github className="w-4 h-4 text-gray-600" />
                    </motion.button>
                  </div>
                </motion.div>
              </div>

              {/* Project Content */}
              <div className="p-8">
                <motion.h3 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.4 }}
                  className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors"
                >
                  {project.title}
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.5 }}
                  className="text-gray-600 mb-6"
                >
                  {project.description}
                </motion.p>

                {/* Technologies */}
                <div className="mb-6">
                  <motion.h4 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 + 0.6 }}
                    className="text-sm font-semibold text-gray-900 mb-3"
                  >
                    Technologies Used
                  </motion.h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <motion.span
                        key={techIndex}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.2 + 0.7 + techIndex * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <AnimatedSection className="text-center mt-16" delay={0.5}>
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white"
          >
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-2xl font-bold mb-4"
            >
              Ready to Start Your Project?
            </motion.h3>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-blue-100 mb-6 max-w-2xl mx-auto"
            >
              Let's discuss how we can help you achieve your business goals with custom software solutions.
            </motion.p>
            <motion.a
              href="/#contact"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </motion.a>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Portfolio;